import AppButton from 'components/Inputs/Buttons';
import useUploadImage from 'hooks/API/POST/useUploadImage';
import useToaster from 'hooks/useToaster';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { getRandomUid } from 'utilities/uuid.service';

function UploadBlock({ inputProps = {}, labelProps = {}, props = {} }) {
  const inputElement = useRef();
  const [file, setFile] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);

  const { handleChange, isPublic, referenceKey, uploadCallback, fileData = {} } = props;
  const disabled = props.disabled || false;
  const { isAudio = false, isVideo = false, isFile = false, isImage = false } = props;

  const { isLoading, isError, mutateAsync, isSuccess } = useUploadImage({
    headers: headers({ isPublic, referenceKey }),
  });
  useToaster(isLoading, { type: isError ? 'error' : 'success', isImage: true });

  const { id, accept, ...rest } = inputProps;
  const { label, ...labelRest } = labelProps;
  const ID = id || getRandomUid();

  const upload = useCallback(async () => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await mutateAsync(formData);

    setUploadedData(response?.data);
  }, [file, mutateAsync]);

  useEffect(() => {
    if (file?.name) {
      upload();
    }
  }, [file?.name, upload]);

  useEffect(() => {
    if (uploadedData?.file) {
      handleChange({
        file,
        uploadedData,
      });
      setFile(null);
      setUploadedData(null);
    }
  }, [file, handleChange, uploadedData]);

  useEffect(() => {
    if (isLoading) {
      uploadCallback?.(true);
    }
    if (isSuccess || isError) {
      uploadCallback?.(false);
    }
  }, [isError, isLoading, isSuccess, uploadCallback]);

  function handleImageChange(event) {
    if (disabled) return; // Prevent file selection if disabled

    const file = event.target.files[0];

    const exts = supportedExtensions();

    // exts length 0 means, no specific file extensions given. All files are accepted.

    if (!accept && exts.length > 0 && !exts.includes(file?.type)) {
      window.alert('The selected file format is not supported.');
      return;
    }

    setFile(file);
    event.target.value = null; // This is enable to select the same image again
  }

  function supportedExtensions() {
    if (isVideo) return acceptedVideoExtensions;
    if (isAudio) return acceptedAudioExtensions;
    if (isImage) return acceptedImageExtensions;
    if (isFile) return [];

    return [];
  }

  const handleButtonClick = () => {
    if (disabled) return; // Prevent click if disabled
    if (inputElement.current) inputElement.current.click();
  };

  const handleKeyDown = (e) => {
    if (disabled) return; // Prevent keyboard interaction if disabled
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputElement.current?.click();
    }
  };

  return (
    <>
      <label className="w-100" htmlFor={`#${ID}#`} {...labelRest}>
        {label}
      </label>
      <AppButton
        variantClass={`btn-right-content upload ${disabled ? 'disabled' : ''}`}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : props?.tabIndex ?? 0}
        disabled={disabled}
      >
        Upload
      </AppButton>
      <input
        type="file"
        id={`#${ID}#`}
        style={{ display: 'none' }}
        ref={inputElement}
        onChange={handleImageChange}
        accept={accept ?? supportedExtensions()}
        disabled={disabled}
        {...rest}
      />
      {isLoading && <div>Uploading...</div>}
      {!isLoading && fileData?.fullPath ? (
        <>
          <br />
          <a
            href={fileData.fullPath}
            rel="noopener noreferrer"
            target="_blank"
            className="__appColor"
            style={{
              fontSize: '13px',
              letterSpacing: '1px',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textDecorationLine: 'underline',
              textDecorationThickness: '3px',
              width: '100%',
              fontStyle: 'italic',
            }}
          >
            Click to view
          </a>
        </>
      ) : null}
    </>
  );
}

const acceptedImageExtensions = ['image/png', 'image/jpeg', 'image/webp'];
const acceptedVideoExtensions = ['video/mp4'];
const acceptedAudioExtensions = ['audio/*', 'audio/mpeg', 'audio/aac', 'audio/vnd.dlna.adts'];

export function headers({ referenceKey = 'ES_LOGO', isPublic = false }) {
  return {
    'X-access-control': isPublic ? 'public' : 'private',
    'X-reference-key': referenceKey,
  };
}

export default memo(UploadBlock);
